import { ResponsiveTable } from '../components/ResponsiveTable';
import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  ChevronLeft, 
  ChevronRight, 
  Edit, 
  Plus,
  Star,
  AlertCircle,
  Loader2,
  Trash2,
  Clock,
  DollarSign,
  MapPin
} from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { getApiUrl } from '../utils/api';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { RootState } from '../store';
import { listItemCollections, type ItemCollection } from '../services/itemCollectionsApi';

interface BodyZone {
  id: string;
  name: string;
  category: string;
  itemCollectionId?: string;
}

interface PiercingType {
  id: string;
  name: string;
  bodyZoneId?: string | null;
  bodyZoneName?: string | null;
  bodyZoneCategory?: string | null;
  itemCollectionId?: string;
  itemCollectionName?: string;
  basePrice: number;
  estimatedDuration: number;
  description?: string;
  ageRestriction: number;
  healingTime?: number;
  productCodes?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PiercingTypesResponse {
  success: boolean;
  data: {
    piercingTypes: PiercingType[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message?: string;
}

export function PiercingTypesListPage() {
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';
  
  const [piercingTypes, setPiercingTypes] = useState<PiercingType[]>([]);
  const [bodyZones, setBodyZones] = useState<BodyZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPiercingType, setEditingPiercingType] = useState<PiercingType | null>(null);
  
  // Filters and pagination
  const [search, setSearch] = useState('');
  const [bodyZoneFilter, setBodyZoneFilter] = useState('');
  const [collectionFilter, setCollectionFilter] = useState('');
  const [collections, setCollections] = useState<ItemCollection[]>([]);
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  const limit = 20;

  const visiblePiercingTypeIds = useMemo(() => piercingTypes.map((p) => p.id), [piercingTypes]);
  const {
    selectedIds: selectedPiercingTypeIds,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visiblePiercingTypeIds);

  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{ isOpen: boolean; ids: string[] }>({
    isOpen: false,
    ids: [],
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ isOpen: boolean; ids: string[] }>({
    isOpen: false,
    ids: [],
  });
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);

  useEffect(() => {
    fetchBodyZones();
    fetchPiercingTypes();
  }, [search, bodyZoneFilter, collectionFilter, sortKey, sortDir, page]);

  useEffect(() => {
    listItemCollections({ limit: 1000, sortKey: 'name', sortDir: 'asc' })
      .then((data) => setCollections(data.itemCollections || []))
      .catch(() => setCollections([]));
  }, []);

  const fetchBodyZones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/body-zones?limit=1000'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setBodyZones(data.data.bodyZones);
        }
      }
    } catch (error) {
      console.error('Fetch body zones error:', error);
    }
  };

  const fetchPiercingTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortKey,
        sortDir,
        ...(search && { search }),
        ...(bodyZoneFilter && { bodyZoneId: bodyZoneFilter }),
        ...(collectionFilter && { itemCollectionId: collectionFilter })
      });

      const response = await fetch(`/api/admin/piercing-types?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch piercing types');
      }

      const data: PiercingTypesResponse = await response.json();
      
      if (data.success) {
        setPiercingTypes(data.data.piercingTypes);
        setTotal(data.data.total);
        setTotalPages(data.data.totalPages);
      } else {
        setError(data.message || 'Failed to load piercing types');
      }
    } catch (error) {
      console.error('Fetch piercing types error:', error);
      setError('Failed to load piercing types');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleCreate = () => {
    setEditingPiercingType(null);
    setShowCreateModal(true);
  };

  const handleEdit = (piercingType: PiercingType) => {
    setEditingPiercingType(piercingType);
    setShowCreateModal(true);
  };

  const handleDelete = async (piercingTypeId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce type de piercing ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/piercing-types/${piercingTypeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchPiercingTypes();
      } else {
        setError('Failed to delete piercing type');
      }
    } catch (error) {
      console.error('Delete piercing type error:', error);
      setError('Failed to delete piercing type');
    }
  };

  const openBulkDeleteConfirm = () => {
    if (selectedPiercingTypeIds.size === 0) return;
    setBulkDeleteConfirm({ isOpen: true, ids: Array.from(selectedPiercingTypeIds) });
  };
  const openBulkHardDeleteConfirm = () => {
    if (selectedPiercingTypeIds.size === 0) return;
    setBulkHardDeleteConfirm({ isOpen: true, ids: Array.from(selectedPiercingTypeIds) });
  };
  const cancelBulkDelete = () => setBulkDeleteConfirm({ isOpen: false, ids: [] });
  const confirmBulkDelete = async () => {
    if (bulkDeleteConfirm.ids.length === 0) return;
    try {
      setBulkDeleting(true);
      setError(null);
      const token = localStorage.getItem('token');
      const failed: string[] = [];

      for (const id of bulkDeleteConfirm.ids) {
        try {
          const resp = await fetch(`/api/admin/piercing-types/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!resp.ok) failed.push(id);
        } catch {
          failed.push(id);
        }
      }

      setBulkDeleteConfirm({ isOpen: false, ids: [] });
      clearSelection();
      await fetchPiercingTypes();

      if (failed.length > 0) {
        setError(`${failed.length} type(s) n’ont pas pu être supprimés.`);
      }
    } finally {
      setBulkDeleting(false);
    }
  };
  const cancelBulkHardDelete = () => setBulkHardDeleteConfirm({ isOpen: false, ids: [] });
  const confirmBulkHardDelete = async () => {
    if (bulkHardDeleteConfirm.ids.length === 0) return;
    try {
      setBulkHardDeleting(true);
      setError(null);
      const token = localStorage.getItem('token');
      const failed: string[] = [];

      for (const id of bulkHardDeleteConfirm.ids) {
        try {
          const resp = await fetch(getApiUrl(`/api/admin/piercing-types/${id}?hardDelete=true`), {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!resp.ok) failed.push(id);
        } catch {
          failed.push(id);
        }
      }

      setBulkHardDeleteConfirm({ isOpen: false, ids: [] });
      clearSelection();
      await fetchPiercingTypes();

      if (failed.length > 0) {
        setError(`${failed.length} type(s) n’ont pas pu être supprimés définitivement.`);
      }
    } finally {
      setBulkHardDeleting(false);
    }
  };

  // Removed unused formatDate function

  if (loading && piercingTypes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement des types de piercing...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Types de Piercing</span>
              <HelpIcon 
                docPath="10-donnees-reference#10-2-types-piercing" 
                tooltip="Documentation : Types de piercing"
              />
            </h1>
            <p className="text-textSecondary mt-1">
              {total} type{total !== 1 ? 's' : ''} au total
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un Type</span>
          </button>
          {selectedCount > 0 && (
            <div className="ml-3 flex items-center space-x-2">
              <button
                onClick={openBulkDeleteConfirm}
                disabled={bulkDeleting || bulkHardDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
                title="Supprimer les types sélectionnés"
              >
                <Trash2 className="w-4 h-4" />
                <span>Supprimer ({selectedCount})</span>
              </button>
              {canHardDelete && (
              <button
                onClick={openBulkHardDeleteConfirm}
                disabled={bulkDeleting || bulkHardDeleting}
                className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                title="Supprimer définitivement les types sélectionnés"
              >
                <AlertCircle className="w-4 h-4" />
                <span>Suppression définitive ({selectedCount})</span>
              </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher un type..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Body Zone Filter */}
          <div className="relative">
            <select
              value={bodyZoneFilter}
              onChange={(e) => setBodyZoneFilter(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Toutes les zones</option>
              <option value="__none__">Sans zone (hors catalogue public)</option>
              {bodyZones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name} ({zone.category})
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={collectionFilter}
              onChange={(e) => { setCollectionFilter(e.target.value); setPage(1); }}
              className="input-field appearance-none pr-10"
            >
              <option value="">Toutes les collections</option>
              {collections.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearch('');
              setBodyZoneFilter('');
              setCollectionFilter('');
              setPage(1);
            }}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Effacer</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button
            onClick={fetchPiercingTypes}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Piercing Types Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={piercingTypes.length === 0 || bulkDeleting || bulkHardDeleting}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    aria-label="Sélectionner tous les types de piercing de la page"
                  />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4" />
                    <span>Piercing</span>
                    {sortKey === 'name' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>Zone du Corps</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Collection
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Codes article
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('basePrice')}
                >
                  <div className="flex items-center space-x-1">
                    <DollarSign className="w-4 h-4" />
                    <span>Prix</span>
                    {sortKey === 'basePrice' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('estimatedDuration')}
                >
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>Durée</span>
                    {sortKey === 'estimatedDuration' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cicatrisation
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {piercingTypes.map((piercingType) => (
                <tr key={piercingType.id} className="group hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedPiercingTypeIds.has(piercingType.id)}
                      disabled={bulkDeleting || bulkHardDeleting}
                      onChange={() => toggleSelect(piercingType.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      aria-label={`Sélectionner le type ${piercingType.name}`}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {piercingType.name}
                      </div>
                      {piercingType.description && (
                        <div className="text-sm text-gray-500 max-w-xs truncate">
                          {piercingType.description}
                        </div>
                      )}
                      {!piercingType.bodyZoneId && (
                        <span className="mt-1 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          Hors catalogue public
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {piercingType.bodyZoneName || '—'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {piercingType.bodyZoneCategory || ''}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {piercingType.itemCollectionName || '—'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-700 max-w-xs truncate" title={piercingType.productCodes || ''}>
                      {piercingType.productCodes || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {piercingType.basePrice}€
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {piercingType.estimatedDuration} min
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {piercingType.healingTime ? `${piercingType.healingTime} sem.` : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(piercingType)}
                        className="text-primary-600 hover:text-primary-700 p-1 rounded"
                        title="Modifier le type"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(piercingType.id)}
                        className="text-red-600 hover:text-red-700 p-1 rounded"
                        title="Supprimer le type"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {/* Empty State */}
        {!loading && piercingTypes.length === 0 && (
          <div className="text-center py-12">
            <Star className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun type trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {search || bodyZoneFilter 
                ? 'Essayez de modifier vos filtres de recherche.'
                : 'Commencez par ajouter votre premier type de piercing.'
              }
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreate}
                className="btn-primary flex items-center space-x-2 mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Ajouter un Type</span>
              </button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="dt-list-toolbar bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de <span className="font-medium">{(page - 1) * limit + 1}</span> à{' '}
                  <span className="font-medium">{Math.min(page * limit, total)}</span> sur{' '}
                  <span className="font-medium">{total}</span> résultats
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <PiercingTypeModal
          piercingType={editingPiercingType}
          bodyZones={bodyZones}
          collections={collections}
          onClose={() => {
            setShowCreateModal(false);
            setEditingPiercingType(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setEditingPiercingType(null);
            fetchPiercingTypes();
          }}
        />
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteConfirm.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer <strong>{bulkDeleteConfirm.ids.length}</strong> type{bulkDeleteConfirm.ids.length !== 1 ? 's' : ''} ?
              Cette action ne peut pas être annulée.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {bulkDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={cancelBulkHardDelete}
        entityLabel="type(s) de piercing"
        count={bulkHardDeleteConfirm.ids.length}
        isLoading={bulkHardDeleting}
      />
    </div>
  );
}

interface PiercingTypeModalProps {
  piercingType: PiercingType | null;
  bodyZones: BodyZone[];
  collections: ItemCollection[];
  onClose: () => void;
  onSuccess: () => void;
}

function PiercingTypeModal({ piercingType, bodyZones, collections, onClose, onSuccess }: PiercingTypeModalProps) {
  const [formData, setFormData] = useState({
    name: piercingType?.name || '',
    bodyZoneId: piercingType?.bodyZoneId || '',
    itemCollectionId: piercingType?.itemCollectionId || collections[0]?.id || '',
    basePrice: piercingType?.basePrice || 0,
    estimatedDuration: piercingType?.estimatedDuration || 30,
    description: piercingType?.description || '',
    ageRestriction: piercingType?.ageRestriction || 18,
    healingTime: piercingType?.healingTime || null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const url = piercingType 
        ? `/api/admin/piercing-types/${piercingType.id}`
        : '/api/admin/piercing-types';
      
      const response = await fetch(url, {
        method: piercingType ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          bodyZoneId: formData.bodyZoneId || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save piercing type');
      }

      const data = await response.json();
      
      if (data.success) {
        onSuccess();
      } else {
        setError(data.message || 'Failed to save piercing type');
      }
    } catch (error) {
      console.error('Save piercing type error:', error);
      setError('Failed to save piercing type');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-textPrimary mb-4">
            {piercingType ? 'Modifier le Type' : 'Ajouter un Type'}
          </h2>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {piercingType?.productCodes && (
            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <span className="font-medium">Codes article :</span> {piercingType.productCodes}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Nom du piercing *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="input-field"
                placeholder="Hélix"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Collection *
              </label>
              <select
                required
                value={formData.itemCollectionId}
                onChange={(e) => setFormData(prev => ({ ...prev, itemCollectionId: e.target.value, bodyZoneId: '' }))}
                className="input-field"
              >
                <option value="">Choisir une collection</option>
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Zone du corps
              </label>
              <select
                value={formData.bodyZoneId || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, bodyZoneId: e.target.value }))}
                className="input-field"
              >
                <option value="">Aucune (hors catalogue public)</option>
                {bodyZones
                  .filter((zone) => !formData.itemCollectionId || zone.itemCollectionId === formData.itemCollectionId)
                  .map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.name} ({zone.category})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Sans zone, le type n’apparaît pas dans la réservation en ligne.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Prix de base (€) *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  step="5"
                  value={formData.basePrice}
                  onChange={(e) => setFormData(prev => ({ ...prev, basePrice: parseFloat(e.target.value) || 0 }))}
                  className="input-field"
                  placeholder="80"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Durée (min) *
                </label>
                <input
                  type="number"
                  required
                  min="15"
                  step="15"
                  value={formData.estimatedDuration}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimatedDuration: parseInt(e.target.value) || 30 }))}
                  className="input-field"
                  placeholder="30"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Âge minimum
                </label>
                <input
                  type="number"
                  min="16"
                  max="21"
                  value={formData.ageRestriction}
                  onChange={(e) => setFormData(prev => ({ ...prev, ageRestriction: parseInt(e.target.value) || 18 }))}
                  className="input-field"
                  placeholder="18"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Cicatrisation (sem.)
                </label>
                <input
                  type="number"
                  min="1"
                  max="52"
                  value={formData.healingTime || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, healingTime: e.target.value ? parseInt(e.target.value) : null }))}
                  className="input-field"
                  placeholder="8"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="input-field"
                placeholder="Description du type de piercing..."
              />
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'Sauvegarde...' : (piercingType ? 'Modifier' : 'Créer')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}